import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { X } from "lucide-react";
import { Suspense } from "react";
import { z } from "zod";

import { EmptyState } from "@/components/empty-state";
import { PercentageBar } from "@/components/percentage-bar";
import { subjectColorAlpha } from "@/components/subject-badge";
import { TableSkeleton } from "@/components/table-skeleton";
import { Button } from "@/components/ui/button";
import { SubjectLabel } from "@/components/ui/subject-label";
import type { SubjectKey } from "@/components/ui/subject-logo";
import { currentRole, pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

interface MarkRow {
  id: string;
  student: string;
  exam: string;
  batch: string;
  type: string;
  correct: number;
  wrong: number;
  unattempted: number;
  total_marks: number;
  percentage: number;
  expand?: {
    student?: { id: string; name: string };
    exam?: { id: string; name: string; type: string };
  };
}

const PER_PAGE = 50;
// TYPES drives the zod enum (must stay permissive so legacy ?type=BOT URLs
// don't 400). VISIBLE_TYPES is what the subject pill bar renders — bot/zoo
// removed to stay consistent with the rest of the masked UI.
const TYPES = ["PHY", "CHE", "BOT", "ZOO", "BIO", "TOTAL", "MODEL"] as const;
const VISIBLE_TYPES = ["PHY", "CHE", "BIO", "TOTAL", "MODEL"] as const;
type MarkType = (typeof TYPES)[number];

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  batch: z.string().optional(),
  exam: z.string().optional(),
  student: z.string().optional(),
  type: z.enum(TYPES).optional(),
});

function pbEscape(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function marksQuery(params: {
  page: number;
  batch: string | undefined;
  exam: string | undefined;
  student: string | undefined;
  type: MarkType | undefined;
  authId: string | undefined;
}) {
  const clauses: string[] = [];
  if (params.batch) clauses.push(`batch = "${pbEscape(params.batch)}"`);
  if (params.exam) clauses.push(`exam = "${pbEscape(params.exam)}"`);
  if (params.student) clauses.push(`student = "${pbEscape(params.student)}"`);
  if (params.type) clauses.push(`type = "${pbEscape(params.type)}"`);
  const filter = clauses.join(" && ");
  return {
    queryKey: [
      "marks",
      "list",
      params.page,
      params.batch ?? null,
      params.exam ?? null,
      params.student ?? null,
      params.type ?? null,
      params.authId ?? "anon",
    ] as const,
    queryFn: () =>
      pb.collection("mark").getList<MarkRow>(params.page, PER_PAGE, {
        sort: "-percentage",
        filter,
        expand: "student,exam",
        fields:
          "id,student,exam,batch,type,correct,wrong,unattempted,total_marks,percentage,expand.student.id,expand.student.name,expand.exam.id,expand.exam.name,expand.exam.type",
      }),
  };
}

export const Route = createFileRoute("/_app/marks/")({
  // Batch-wide ranked marks table — deep-linkable but not for students, who
  // may only see their own marks. Teacher/support unrestricted.
  beforeLoad: () => {
    if (currentRole() === "student") {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page,
    batch: search.batch,
    exam: search.exam,
    student: search.student,
    type: search.type,
  }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(marksQuery({ ...deps, authId: pb.authStore.model?.id })),
  component: MarksPage,
});

function MarksPage() {
  useDocumentTitle("Marks");
  const { page, batch, exam, student, type } = Route.useSearch();
  const filterDescription = (() => {
    const parts: string[] = [];
    if (batch) parts.push("one batch");
    if (exam) parts.push("one exam");
    if (student) parts.push("one student");
    if (type) parts.push(`type=${type}`);
    if (parts.length === 0) return "Across every batch you can see.";
    return `Filtered to ${parts.join(" + ")}.`;
  })();
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Marks</h1>
        <p className="text-muted-foreground text-sm">{filterDescription}</p>
      </div>
      <Toolbar batch={batch} exam={exam} student={student} type={type} />
      <Suspense fallback={<ListSkeleton />}>
        <MarksList page={page} batch={batch} exam={exam} student={student} type={type} />
      </Suspense>
    </div>
  );
}

function Toolbar({
  batch,
  exam,
  student,
  type,
}: {
  batch: string | undefined;
  exam: string | undefined;
  student: string | undefined;
  type: MarkType | undefined;
}) {
  const without = (drop: "batch" | "exam" | "student" | "type") => ({
    page: 1,
    ...(drop !== "batch" && batch ? { batch } : {}),
    ...(drop !== "exam" && exam ? { exam } : {}),
    ...(drop !== "student" && student ? { student } : {}),
    ...(drop !== "type" && type ? { type } : {}),
  });
  const withType = (t: MarkType) => ({
    page: 1,
    ...(batch ? { batch } : {}),
    ...(exam ? { exam } : {}),
    ...(student ? { student } : {}),
    type: t,
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">Subject:</span>
      {VISIBLE_TYPES.map((t) => (
        <Button key={t} asChild variant={type === t ? "default" : "outline"} size="sm">
          <Link to="/marks" search={withType(t)}>
            {t}
          </Link>
        </Button>
      ))}
      {batch ? (
        <Button asChild variant="outline" size="sm">
          <Link to="/marks" search={without("batch")}>
            <X />
            Clear batch
          </Link>
        </Button>
      ) : null}
      {exam ? (
        <Button asChild variant="outline" size="sm">
          <Link to="/marks" search={without("exam")}>
            <X />
            Clear exam
          </Link>
        </Button>
      ) : null}
      {student ? (
        <Button asChild variant="outline" size="sm">
          <Link to="/marks" search={without("student")}>
            <X />
            Clear student
          </Link>
        </Button>
      ) : null}
      {type ? (
        <Button asChild variant="ghost" size="sm">
          <Link to="/marks" search={without("type")}>
            <X />
            Clear type
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

function MarksList({
  page,
  batch,
  exam,
  student,
  type,
}: {
  page: number;
  batch: string | undefined;
  exam: string | undefined;
  student: string | undefined;
  type: MarkType | undefined;
}) {
  const { data } = useSuspenseQuery(
    marksQuery({ page, batch, exam, student, type, authId: pb.authStore.model?.id }),
  );

  if (data.items.length === 0) {
    return (
      <EmptyState title="No marks match" description="Try a different subject or clear a filter." />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-2 font-medium">Student</th>
              <th className="px-4 py-2 font-medium">Exam</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 text-right font-medium">Correct</th>
              <th className="px-4 py-2 text-right font-medium">Wrong</th>
              <th className="px-4 py-2 text-right font-medium">Skipped</th>
              <th className="px-4 py-2 text-right font-medium">Marks</th>
              <th className="px-4 py-2 text-right font-medium">%</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((m) => (
              <tr
                key={m.id}
                className="subject-row border-t transition-colors"
                style={{ ["--row-tint" as never]: subjectColorAlpha(m.type, 0.12) }}
              >
                <td className="px-4 py-2">
                  <Link
                    to="/marks/$markId"
                    params={{ markId: m.id }}
                    preload="intent"
                    className="hover:underline"
                  >
                    {m.expand?.student?.name ?? m.student}
                  </Link>
                </td>
                <td className="text-muted-foreground px-4 py-2">
                  {m.expand?.exam?.name ?? m.exam}
                </td>
                <td className="px-4 py-2">
                  <SubjectLabel subject={m.type.toLowerCase() as SubjectKey} size="sm" />
                </td>
                <td className="px-4 py-2 text-right font-mono">{m.correct}</td>
                <td className="text-destructive px-4 py-2 text-right font-mono">{m.wrong}</td>
                <td className="text-muted-foreground px-4 py-2 text-right font-mono">
                  {m.unattempted}
                </td>
                <td className="px-4 py-2 text-right font-mono">{m.total_marks}</td>
                <td className="px-4 py-2 text-right">
                  {m.percentage < 0 ? (
                    <span className="text-muted-foreground font-mono">—</span>
                  ) : (
                    <PercentageBar value={m.percentage} type={m.type} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={data.totalPages}
        total={data.totalItems}
        batch={batch}
        exam={exam}
        student={student}
        type={type}
      />
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  batch,
  exam,
  student,
  type,
}: {
  page: number;
  totalPages: number;
  total: number;
  batch: string | undefined;
  exam: string | undefined;
  student: string | undefined;
  type: MarkType | undefined;
}) {
  const search = (p: number) => ({
    page: p,
    ...(batch ? { batch } : {}),
    ...(exam ? { exam } : {}),
    ...(student ? { student } : {}),
    ...(type ? { type } : {}),
  });
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">
        Page {page} of {totalPages.toLocaleString()} · {total.toLocaleString()} mark
        {total === 1 ? "" : "s"} (sorted by % desc)
      </span>
      <div className="flex gap-2">
        <Button asChild variant="outline" size="sm" disabled={page <= 1}>
          {page <= 1 ? (
            <span aria-disabled="true">Previous</span>
          ) : (
            <Link to="/marks" search={search(page - 1)} preload="intent">
              Previous
            </Link>
          )}
        </Button>
        <Button asChild variant="outline" size="sm" disabled={page >= totalPages}>
          {page >= totalPages ? (
            <span aria-disabled="true">Next</span>
          ) : (
            <Link to="/marks" search={search(page + 1)} preload="intent">
              Next
            </Link>
          )}
        </Button>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return <TableSkeleton columns={8} />;
}
